/**
 * @file capture/boardroomDraftStore.ts
 * @brief IndexedDB durable store for in-progress web boardroom recordings.
 *
 * @details
 * Mirrors mobile crash-recovery intent: chunks flush continuously so a
 * refresh / force-quit leaves a Process / Continue / Discard path. Each chunk
 * is written once to its own row; the draft row holds metadata only (v2).
 * One active draft per user (new start replaces unrecovered drafts).
 *
 * @author DES
 * @date 28-07-2026
 */

import type { AgendaCheck } from "../agendaChecks";

const DB_NAME = "verblizr-boardroom-drafts";
const DB_VERSION = 2;
const STORE = "drafts";
/** v2: one row per MediaRecorder chunk, keyed [draftId, seq]. */
const CHUNK_STORE = "chunks";

/** Align with mobile 6h resume window. */
export const DRAFT_RESUME_WINDOW_MS = 6 * 60 * 60 * 1000;

export type BoardroomDraftBookmark = {
  id: string;
  type: "decision" | "todo";
  timeInSecs: number;
  timestamp: string;
};

export type BoardroomDraftParticipant = {
  id?: string;
  name: string;
  email: string;
  isExternal: boolean;
  shared?: boolean;
  profileImageUrl?: string;
  isRegisteredUser?: boolean;
  nativeLanguage?: string | null;
};

/** Meeting Notes (private, lighter) or full Meeting Intelligence (ADR-030). */
export type BoardroomMeetingTier = "notes" | "full";

export type BoardroomDraftMeta = {
  draftId: string;
  userId: string;
  title: string;
  targetLanguages: string[];
  participants: BoardroomDraftParticipant[];
  bookmarks: BoardroomDraftBookmark[];
  mimeType: string;
  contentType: string;
  extension: string;
  /** Accumulated elapsed ms while recording (excludes hold). */
  elapsedMs: number;
  createdAt: number;
  lastActiveAt: number;
  /** deviceId used for getUserMedia, if any */
  deviceId?: string;
  /**
   * Desktop app: call audio (Zoom/Teams/Meet) is mixed into this recording.
   * Kept in the draft so a meeting resumed after a crash keeps hearing the
   * call instead of silently dropping to mic only. (30-09-2026)
   */
  includeSystemAudio?: boolean;
  status: "recording" | "held" | "interrupted";
  /**
   * Stripe hold from POST /billing/preauthorize at record start.
   * Reused on Continue — never place a second hold for one meeting.
   */
  paymentIntentId?: string | null;
  authorizedCents?: number | null;
  /** When recording was started from a scheduled meeting (Phase C). */
  scheduledMeetingId?: string | null;
  /** GDPR Art 13 (06-10-2026): how and when the recording notice was
   *  confirmed. In the draft so a recovered recording keeps it. */
  recordingNotice?: { method: "setup_slide" | "scheduled_confirm"; at: string } | null;
  /**
   * Meeting Notes or full Meeting Intelligence (ADR-030, 06-10-2026). In the
   * draft so a recording recovered after a crash is created at the tier its
   * owner chose. Absent on older drafts = full.
   * Related: captureApi.ts (sent on create), BoardroomSetupPage.tsx (chosen)
   */
  tier?: BoardroomMeetingTier;
  /**
   * Agenda chosen on the setup screen, attached once the meeting exists
   * (ADR-023). Carried in the DRAFT and not only in router state, so a
   * recording recovered after a crash still knows what its agenda was.
   */
  prepNoteId?: string | null;
  /**
   * Every agenda note, in the order chosen (30-09-2026, mobile parity: a
   * meeting takes up to MAX_AGENDA_NOTES). Supersedes `prepNoteId`, which is
   * still read so a draft saved before this change keeps its agenda.
   */
  prepNoteIds?: string[];
  /**
   * B49: the agenda check boxes, ticked with the recording clock (like the
   * bookmarks). In the draft so a recovered recording keeps its ticks.
   * Absent / null = no check boxes were shown (the upload leaves it out).
   */
  agendaChecks?: AgendaCheck[] | null;
};

/** Mobile's limit (FRONTEND PrepNotePickerModal MAX_AGENDA_NOTES). */
export const MAX_AGENDA_NOTES = 5;

/** A draft's agenda notes, whichever field an older or newer draft used. */
export function draftAgendaIds(
  meta: Pick<BoardroomDraftMeta, "prepNoteId" | "prepNoteIds">,
): string[] {
  if (Array.isArray(meta.prepNoteIds) && meta.prepNoteIds.length > 0) {
    return [...new Set(meta.prepNoteIds.filter(Boolean))];
  }
  return meta.prepNoteId ? [meta.prepNoteId] : [];
}

/** A draft as callers see it: the metadata plus every chunk, in order. */
export type DraftRecord = BoardroomDraftMeta & {
  /** Ordered MediaRecorder blobs */
  chunks: Blob[];
};

/** One MediaRecorder chunk, stored once and never rewritten. */
type ChunkRow = {
  draftId: string;
  seq: number;
  blob: Blob;
};

function chunkRange(draftId: string): IDBKeyRange {
  return IDBKeyRange.bound([draftId, 0], [draftId, Number.MAX_SAFE_INTEGER]);
}

/**
 * Version 2 (30-09-2026) moves chunks out of the draft row. Version 1 kept the
 * whole `chunks` array inside it, and every flush (one per 2s chunk) re-put the
 * row, so Chromium rewrote EVERY chunk blob each time: 14 minutes into a
 * desktop-app meeting that was ~41 MB of disk writes every 2 seconds, and it
 * crashed the browser process's storage thread (Electron, 30-09-2026 13:52). Now a chunk is
 * written once (`appendChunk`) and the row holds only metadata.
 *
 * The upgrade moves any v1 draft's inline chunks into the chunk store in the
 * same versionchange transaction, so a recording interrupted before this
 * change is still recoverable. If the move fails, the transaction aborts, the
 * database stays at v1 and nothing is lost.
 */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      const tx = req.transaction;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "draftId" });
      }
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        db.createObjectStore(CHUNK_STORE, { keyPath: ["draftId", "seq"] });
      }
      if (!tx) return;
      const drafts = tx.objectStore(STORE);
      const chunks = tx.objectStore(CHUNK_STORE);
      drafts.openCursor().onsuccess = (ev) => {
        const cursor = (ev.target as IDBRequest<IDBCursorWithValue | null>).result;
        if (!cursor) return;
        const row = cursor.value as BoardroomDraftMeta & { chunks?: Blob[] };
        if (Array.isArray(row.chunks)) {
          row.chunks.forEach((blob, seq) => {
            chunks.put({ draftId: row.draftId, seq, blob } satisfies ChunkRow);
          });
          const { chunks: _moved, ...meta } = row;
          cursor.update(meta);
        }
        cursor.continue();
      };
    };
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB tx failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB tx aborted"));
  });
}

function requestResult<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Save the draft's metadata. Cheap and safe to call often: chunks are NOT part
 * of it (see `appendChunk`), so the cost no longer grows with the meeting.
 */
export async function putDraft(meta: BoardroomDraftMeta): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(meta);
    await txDone(tx);
  } finally {
    db.close();
  }
}

/** Store one MediaRecorder chunk at position `seq` (0-based, in record order). */
export async function appendChunk(draftId: string, seq: number, blob: Blob): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(CHUNK_STORE, "readwrite");
    tx.objectStore(CHUNK_STORE).put({ draftId, seq, blob } satisfies ChunkRow);
    await txDone(tx);
  } finally {
    db.close();
  }
}

export async function getDraft(draftId: string): Promise<DraftRecord | null> {
  const db = await openDb();
  try {
    const tx = db.transaction([STORE, CHUNK_STORE], "readonly");
    const metaReq = tx.objectStore(STORE).get(draftId);
    const chunkReq = tx.objectStore(CHUNK_STORE).getAll(chunkRange(draftId));
    const [meta, rows] = await Promise.all([requestResult(metaReq), requestResult(chunkReq)]);
    await txDone(tx);
    if (!meta) return null;
    // getAll over the [draftId, seq] key returns rows in seq order.
    const chunks = (rows as ChunkRow[]).map((r) => r.blob);
    return { ...(meta as BoardroomDraftMeta), chunks };
  } finally {
    db.close();
  }
}

export async function deleteDraft(draftId: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction([STORE, CHUNK_STORE], "readwrite");
    tx.objectStore(STORE).delete(draftId);
    tx.objectStore(CHUNK_STORE).delete(chunkRange(draftId));
    await txDone(tx);
  } finally {
    db.close();
  }
}

/** Merge fields into an existing draft (e.g. attach billing hold before Process). */
export async function patchDraftMeta(
  draftId: string,
  patch: Partial<BoardroomDraftMeta>,
): Promise<BoardroomDraftMeta | null> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const row = (await requestResult(store.get(draftId))) as BoardroomDraftMeta | undefined;
    if (!row) {
      await txDone(tx);
      return null;
    }
    const next: BoardroomDraftMeta = { ...row, ...patch, draftId: row.draftId };
    store.put(next);
    await txDone(tx);
    return next;
  } finally {
    db.close();
  }
}

export async function listDraftsForUser(userId: string): Promise<BoardroomDraftMeta[]> {
  const db = await openDb();
  try {
    const tx = db.transaction([STORE, CHUNK_STORE], "readonly");
    const rows = (await requestResult(tx.objectStore(STORE).getAll())) as BoardroomDraftMeta[];
    const now = Date.now();
    const candidates = rows
      .filter((r) => r.userId === userId)
      .filter((r) => now - r.lastActiveAt <= DRAFT_RESUME_WINDOW_MS);
    // Count, don't load: listing must not pull an hour of audio into memory.
    const counts = await Promise.all(
      candidates.map((r) =>
        requestResult(tx.objectStore(CHUNK_STORE).count(chunkRange(r.draftId))),
      ),
    );
    await txDone(tx);
    return candidates
      .filter((r, i) => counts[i] > 0 || r.elapsedMs > 0)
      .sort((a, b) => b.lastActiveAt - a.lastActiveAt);
  } finally {
    db.close();
  }
}

/** Build a single Blob from stored chunks for upload / playback. */
export async function assembleDraftBlob(
  draftId: string,
): Promise<{ blob: Blob; meta: BoardroomDraftMeta } | null> {
  const row = await getDraft(draftId);
  if (!row || row.chunks.length === 0) return null;
  const blob = new Blob(row.chunks, {
    type: row.contentType || row.mimeType || "audio/webm",
  });
  const { chunks: _c, ...meta } = row;
  return { blob, meta };
}

export function newDraftId(): string {
  return `wd_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}
